import React, { useMemo } from "react";
import "./markdown.css";

interface MarkdownRendererProps {
    markdown: string;
    className?: string;
}

function parseMarkdownToHtml(md: string) {
    const lines = md.split("\n");
    const result: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        // Table detection: look for a line with pipes and a following separator row
        if (line.includes("|") && i + 1 < lines.length && /^\|?[\s\-|:]+\|/.test(lines[i + 1])) {
            const tableLines: string[] = [];
            while (i < lines.length && lines[i].includes("|")) {
                tableLines.push(lines[i]);
                i++;
            }

            const [headerRow, , ...bodyRows] = tableLines;
            const headers = parseTableRow(headerRow);
            const rows = bodyRows.map(parseTableRow);

            result.push(
                <>
                    {/*<br></br>*/}
                    <table key={i}>
                        <thead>
                            <tr>
                                {headers.map((h, hi) => (
                                    <th key={hi}>{inlineMarkdown(h)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, ri) => (
                                <tr key={ri}>
                                    {row.map((cell, ci) => (
                                        <td key={ci}>{inlineMarkdown(cell)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <br></br>
                </>
            );
            continue;
        }
        // Horizontal rule
        if (/^---+$/.test(line.trim())) {
            result.push(<hr />);
            i++;
            continue;
        }

        // Headings
        const h3 = line.match(/^### (.+)/);
        if (h3) {
            result.push(<h3>{inlineMarkdown(h3[1])}</h3>);
            i++;
            continue;
        }

        const h2 = line.match(/^## (.+)/);
        if (h2) {
            result.push(<h2>{inlineMarkdown(h2[1])}</h2>);
            i++;
            continue;
        }

        const h1 = line.match(/^# (.+)/);
        if (h1) {
            result.push(<h1>{inlineMarkdown(h1[1])}</h1>);
            i++;
            continue;
        }

        // Unordered list
        if (/^[*-] /.test(line)) {
            const items: React.ReactNode[] = [];
            let li = 0;
            while (i < lines.length && /^[*-] /.test(lines[i])) {
                items.push(<li key={li++}>{inlineMarkdown(lines[i].replace(/^[*-] /, ""))}</li>);
                i++;
            }
            result.push(<ul key={i}>{items}</ul>);
            continue;
        }

        // Ordered list
        if (/^\d+\. /.test(line)) {
            const items: React.ReactNode[] = [];
            let li = 0;
            while (i < lines.length && /^\d+\. /.test(lines[i])) {
                items.push(<li key={li++}>{inlineMarkdown(lines[i].replace(/^\d+\. /, ""))}</li>);
                i++;
            }
            result.push(<ol key={i}>{items}</ol>);
            continue;
        }

        // Blockquote
        if (line.startsWith("> ")) {
            result.push(<blockquote>{inlineMarkdown(line.slice(2))}</blockquote>);
            i++;
            continue;
        }

        // Fenced code block
        if (line.startsWith("```")) {
            const lang = line.slice(3).trim();
            const codeLines: string[] = [];
            i++;
            while (i < lines.length && !lines[i].startsWith("```")) {
                codeLines.push(lines[i]);
                i++;
            }
            i++; // skip closing ```
            result.push(
                <pre key={i}>
                    <code className={lang ? `language-${lang}` : undefined}>
                        {codeLines.join("\n")}
                    </code>
                </pre>
            );
            continue;
        }

        // Empty line
        if (line.trim() === "") {
            result.push(<></>); // <br />
            i++;
            continue;
        }

        // Paragraph
        result.push(<p>{inlineMarkdown(line)}</p>);
        i++;
    }

    return result;
}

function parseTableRow(row: string): string[] {
    return row
        .split("|")
        .map((cell) => cell.trim())
        .filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1);
}

/* eslint-disable react/display-name */
const INLINE_RULES: { pattern: RegExp; render: (...groups: string[]) => React.ReactNode }[] = [
    {
        pattern: /\*\*\*(.+?)\*\*\*/g,
        render: (_, g) => (
            <strong>
                <em>{g}</em>
            </strong>
        ),
    },
    { pattern: /\*\*(.+?)\*\*/g, render: (_, g) => <strong>{g}</strong> },
    { pattern: /\*(.+?)\*/g, render: (_, g) => <em>{g}</em> },
    { pattern: /`(.+?)`/g, render: (_, g) => <code>{g}</code> },
    { pattern: /~~(.+?)~~/g, render: (_, g) => <del>{g}</del> },
    { pattern: /\[(.+?)\]\((.+?)\)/g, render: (_, label, href) => <a href={href}>{label}</a> },
];
/* eslint-enable react/display-name */

function inlineMarkdown(text: string): React.ReactNode {
    let current: React.ReactNode[] = [text];

    INLINE_RULES.forEach(({ pattern, render }, ri) => {
        const next = current.flatMap((node, ni) => {
            if (typeof node !== "string") return [node];

            const parts: React.ReactNode[] = [];
            let last = 0;
            let match;
            pattern.lastIndex = 0;

            while ((match = pattern.exec(node)) !== null) {
                if (match.index > last) parts.push(node.slice(last, match.index));
                parts.push(<span key={`${ri}-${ni}-${match.index}`}>{render(...match)}</span>);
                last = match.index + match[0].length;
            }

            if (last < node.length) parts.push(node.slice(last));
            return parts.length ? parts : [node];
        });
        current = next;
    });

    return <>{current}</>;
}

import { cloneElement } from "react";

function withKeys(elements) {
    return elements.map((el, index) => cloneElement(el, { key: el.key ?? index }));
}

export default function MarkdownRenderer({ markdown }: MarkdownRendererProps) {
    const html = useMemo(() => withKeys(parseMarkdownToHtml(markdown)), [markdown]);

    return <div className={"markdown"}>{html}</div>;
}
